/**
 * WC-A3 — leadership communications oversight (Layer A accountability).
 * Pure aggregation + severity/filter helpers; server enriches with profile names.
 */
import { LEAD_HYGIENE_NUDGE_KIND } from "./leadFollowupHygiene.js";
import { OWNERSHIP_DIGEST_KIND } from "./ownershipDigestModel.js";
import {
  aggregateWorkplaceCommsDiagnostics,
  type AckBacklogUserRow,
  type WorkplaceCommsDiagnosticsSnapshot,
} from "./workplaceCommsDiagnostics.js";
import { notificationCountsAsBlockingAck } from "./salesChecklistAckBlocking.js";

export const OVERSIGHT_NUDGE_KINDS = new Set([
  "sales_checklist_nudge",
  LEAD_HYGIENE_NUDGE_KIND,
  OWNERSHIP_DIGEST_KIND,
  "task_assigned",
  "lead_reminder",
  "user_reminder",
]);

export type OversightSeverity = "high" | "medium" | "low" | "info";

export type OversightFilters = {
  from: string;
  to: string;
  severity: "all" | OversightSeverity;
  kind: string | null;
  user_id: string | null;
};

export type OversightDeepLink = {
  lead_id?: string;
  deal_id?: string;
  thread_id?: string;
  task_id?: string;
  notification_id?: string;
};

export type OversightNudgeRow = {
  id: string;
  kind: string;
  title: string;
  body: string;
  user_id: string;
  display_name: string;
  severity: OversightSeverity;
  created_at: string;
  read_at: string | null;
  requires_ack: boolean;
  acknowledged_at: string | null;
  deep_link: OversightDeepLink;
};

export type OversightHygieneResolutionRow = {
  id: string;
  lead_id: string;
  lead_label: string;
  actor_user_id: string | null;
  actor_display_name: string;
  action: string;
  reason: string | null;
  cadence_days: number | null;
  severity: OversightSeverity;
  created_at: string;
  deep_link: OversightDeepLink;
};

export type OversightBlockedUserRow = {
  user_id: string;
  display_name: string;
  pending_ack_count: number;
  severity: OversightSeverity;
};

export type WorkplaceCommsOversightSnapshot = {
  filters: OversightFilters;
  diagnostics: WorkplaceCommsDiagnosticsSnapshot;
  blocked_checklist_users: OversightBlockedUserRow[];
  ack_backlog_by_user: OversightBlockedUserRow[];
  recent_nudges: OversightNudgeRow[];
  hygiene_resolutions: OversightHygieneResolutionRow[];
};

type RawNotificationRow = {
  id?: string;
  kind?: string | null;
  title?: string | null;
  body?: string | null;
  user_id?: string | null;
  deal_id?: string | null;
  thread_id?: string | null;
  task_id?: string | null;
  created_at?: string | null;
  read_at?: string | null;
  requires_ack?: boolean | null;
  acknowledged_at?: string | null;
  archived_at?: string | null;
  ack_deadline_at?: string | null;
  reminder_status?: string | null;
};

type RawHygieneEventRow = {
  id?: string;
  lead_id?: string;
  actor_user_id?: string | null;
  created_at?: string;
  payload?: Record<string, unknown> | null;
};

export function defaultOversightFromDate(referenceMs: number = Date.now()): string {
  const d = new Date(referenceMs);
  d.setUTCDate(d.getUTCDate() - 7);
  return d.toISOString();
}

export function classifyNotificationSeverity(
  row: RawNotificationRow,
  nowMs: number = Date.now()
): OversightSeverity {
  if (notificationCountsAsBlockingAck(row, nowMs)) return "high";
  const kind = String(row.kind || "").trim();
  if (kind === LEAD_HYGIENE_NUDGE_KIND && String(row.reminder_status || "open") === "open") {
    return "medium";
  }
  if (kind === "sales_checklist_nudge" || kind === OWNERSHIP_DIGEST_KIND) return "medium";
  if (kind === "task_assigned") return "medium";
  if (row.requires_ack === true && !row.acknowledged_at) return "medium";
  return "info";
}

export function severityMatchesFilter(
  severity: OversightSeverity,
  filter: OversightFilters["severity"]
): boolean {
  if (filter === "all") return true;
  return severity === filter;
}

export function buildOversightDeepLink(row: {
  notificationId?: string | null;
  leadId?: string | null;
  dealId?: string | null;
  threadId?: string | null;
  taskId?: string | null;
}): OversightDeepLink {
  const link: OversightDeepLink = {};
  const lid = row.leadId?.trim();
  const did = row.dealId?.trim();
  const tid = row.threadId?.trim();
  const taskId = row.taskId?.trim();
  const nid = row.notificationId?.trim();
  if (lid) link.lead_id = lid;
  if (did) link.deal_id = did;
  if (tid) link.thread_id = tid;
  if (taskId) link.task_id = taskId;
  if (nid) link.notification_id = nid;
  return link;
}

function leadRefFromNotification(row: RawNotificationRow): string | null {
  const kind = String(row.kind || "").trim();
  if (kind === LEAD_HYGIENE_NUDGE_KIND || kind === "lead_reminder") {
    const id = typeof row.deal_id === "string" ? row.deal_id.trim() : "";
    return id || null;
  }
  return null;
}

function dealRefFromNotification(row: RawNotificationRow): string | null {
  const kind = String(row.kind || "").trim();
  if (kind === LEAD_HYGIENE_NUDGE_KIND || kind === "lead_reminder") return null;
  const id = typeof row.deal_id === "string" ? row.deal_id.trim() : "";
  return id || null;
}

export function isOversightNudgeRow(row: RawNotificationRow): boolean {
  const kind = String(row.kind || "").trim();
  if (!kind) return false;
  if (OVERSIGHT_NUDGE_KINDS.has(kind)) return true;
  if (row.thread_id) return false;
  return kind.endsWith("_nudge") || kind.endsWith("_digest");
}

export function normalizeOversightNudgeRow(
  row: RawNotificationRow,
  nameByUser: ReadonlyMap<string, string>,
  nowMs?: number
): OversightNudgeRow | null {
  const id = typeof row.id === "string" ? row.id.trim() : "";
  const user_id = typeof row.user_id === "string" ? row.user_id.trim() : "";
  const created_at = typeof row.created_at === "string" ? row.created_at : "";
  if (!id || !user_id || !created_at) return null;
  const kind = String(row.kind || "unknown").trim();
  const severity = classifyNotificationSeverity(row, nowMs);
  const leadId = leadRefFromNotification(row);
  const dealId = dealRefFromNotification(row);
  return {
    id,
    kind,
    title: String(row.title || "").slice(0, 500),
    body: String(row.body || "").slice(0, 500),
    user_id,
    display_name: nameByUser.get(user_id) ?? user_id.slice(0, 8),
    severity,
    created_at,
    read_at: typeof row.read_at === "string" ? row.read_at : null,
    requires_ack: row.requires_ack === true,
    acknowledged_at: typeof row.acknowledged_at === "string" ? row.acknowledged_at : null,
    deep_link: buildOversightDeepLink({
      notificationId: id,
      leadId,
      dealId,
      threadId: typeof row.thread_id === "string" ? row.thread_id : null,
      taskId: typeof row.task_id === "string" ? row.task_id : null,
    }),
  };
}

export function normalizeHygieneResolutionRow(
  row: RawHygieneEventRow,
  leadLabelById: ReadonlyMap<string, string>,
  nameByUser: ReadonlyMap<string, string>
): OversightHygieneResolutionRow | null {
  const payload = row.payload && typeof row.payload === "object" ? row.payload : null;
  if (payload?.via !== "lead_hygiene_nudge") return null;
  const id = typeof row.id === "string" ? row.id.trim() : "";
  const lead_id = typeof row.lead_id === "string" ? row.lead_id.trim() : "";
  const created_at = typeof row.created_at === "string" ? row.created_at : "";
  if (!id || !lead_id || !created_at) return null;
  const actor_user_id =
    typeof row.actor_user_id === "string" && row.actor_user_id.trim() ? row.actor_user_id.trim() : null;
  const action = typeof payload.action === "string" ? payload.action : "unknown";
  const reason = typeof payload.reason === "string" ? payload.reason : null;
  const cadence =
    typeof payload.cadence_days === "number" && Number.isFinite(payload.cadence_days)
      ? payload.cadence_days
      : null;
  return {
    id,
    lead_id,
    lead_label: leadLabelById.get(lead_id) ?? lead_id.slice(0, 8),
    actor_user_id,
    actor_display_name: actor_user_id
      ? (nameByUser.get(actor_user_id) ?? actor_user_id.slice(0, 8))
      : "System",
    action,
    reason,
    cadence_days: cadence,
    severity: action === "resolve" ? "medium" : "info",
    created_at,
    deep_link: buildOversightDeepLink({ leadId: lead_id }),
  };
}

export function aggregateWorkplaceCommsOversight(input: {
  filters: OversightFilters;
  ackBlockingEnabled: boolean;
  floodGuardrailsEnabled: boolean;
  floodKindLimits: Record<string, number>;
  ackRows: readonly RawNotificationRow[];
  nudgeRows: readonly RawNotificationRow[];
  hygieneEvents: readonly RawHygieneEventRow[];
  nameByUser: ReadonlyMap<string, string>;
  leadLabelById: ReadonlyMap<string, string>;
  nowMs?: number;
}): WorkplaceCommsOversightSnapshot {
  const nowMs = input.nowMs ?? Date.now();
  const diagnostics = aggregateWorkplaceCommsDiagnostics({
    ackBlockingEnabled: input.ackBlockingEnabled,
    floodGuardrailsEnabled: input.floodGuardrailsEnabled,
    floodKindLimits: input.floodKindLimits,
    ackRows: input.ackRows,
    nowMs,
  });

  const ack_backlog_by_user: OversightBlockedUserRow[] = diagnostics.ack_backlog_by_user.map((row: AckBacklogUserRow) => ({
    user_id: row.user_id,
    display_name: input.nameByUser.get(row.user_id) ?? row.user_id.slice(0, 8),
    pending_ack_count: row.pending_ack_count,
    severity: "high" as const,
  }));

  const blocked_checklist_users = input.ackBlockingEnabled ? ack_backlog_by_user : [];

  const fromMs = Date.parse(input.filters.from);
  const toMs = Date.parse(input.filters.to);

  const recent_nudges = input.nudgeRows
    .filter((row) => isOversightNudgeRow(row))
    .filter((row) => {
      const ms = Date.parse(String(row.created_at || ""));
      if (Number.isNaN(fromMs) || Number.isNaN(toMs) || Number.isNaN(ms)) return true;
      return ms >= fromMs && ms <= toMs;
    })
    .map((row) => normalizeOversightNudgeRow(row, input.nameByUser, nowMs))
    .filter((row): row is OversightNudgeRow => row != null)
    .filter((row) => severityMatchesFilter(row.severity, input.filters.severity))
    .filter((row) => !input.filters.kind || row.kind === input.filters.kind)
    .filter((row) => !input.filters.user_id || row.user_id === input.filters.user_id)
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
    .slice(0, 100);

  const hygiene_resolutions = input.hygieneEvents
    .map((row) => normalizeHygieneResolutionRow(row, input.leadLabelById, input.nameByUser))
    .filter((row): row is OversightHygieneResolutionRow => row != null)
    .filter((row) => {
      const ms = Date.parse(row.created_at);
      if (Number.isNaN(fromMs) || Number.isNaN(toMs) || Number.isNaN(ms)) return true;
      return ms >= fromMs && ms <= toMs;
    })
    .filter((row) => severityMatchesFilter(row.severity, input.filters.severity))
    .filter((row) => !input.filters.user_id || row.actor_user_id === input.filters.user_id)
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
    .slice(0, 100);

  return {
    filters: input.filters,
    diagnostics,
    blocked_checklist_users,
    ack_backlog_by_user,
    recent_nudges,
    hygiene_resolutions,
  };
}
